import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    fetchUnreadCount,
    fetchNotifications,
    markAsRead,
    cleanNotifications,
    deleteNotification as deleteNotificationService
} from '../services/notificationService';
import { fetchAllNextTokenPages } from '../utils/paginatedFetch';
import { adaptNotification } from '../adapters/notificationAdapter';
import { isLicenseSharingInviteNotification } from '../utils/notificationTypes';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        refreshUnreadCount();
        const interval = setInterval(refreshUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    async function refreshUnreadCount() {
        try {
            const { count } = await fetchUnreadCount();
            setUnreadCount(count);
        } catch {
            setUnreadCount(0);
        }
    }

    async function loadNotifications() {
        setLoading(true);
        try {
            const { items } = await fetchAllNextTokenPages((nextToken) =>
                fetchNotifications({ limit: 50, nextToken })
            );
            setNotifications(items.map(adaptNotification));
        } finally {
            setLoading(false);
        }
    }

    async function markNotificationAsRead(notification) {
        await markAsRead(notification.id, notification.timestamp);

        setNotifications(prev =>
            prev.map(n =>
                n.id === notification.id ? { ...n, isRead: true } : n
            )
        );

        refreshUnreadCount();
    }

    async function deleteSingleNotification(notification, options = {}) {
        const { allowProtected = false } = options;

        if (!allowProtected && isLicenseSharingInviteNotification(notification)) {
            throw new Error(
                'Convites de licença não podem ser excluídos. Aceite ou recuse o convite.'
            );
        }

        await deleteNotificationService(notification.id, notification.timestamp, {
            confirmLicenseAction: allowProtected,
        });

        setNotifications(prev =>
            prev.filter(n => n.id !== notification.id)
        );

        refreshUnreadCount();
    }

    async function clearAllNotifications() {
        const result = await cleanNotifications();
        setNotifications(prev => prev.filter(isLicenseSharingInviteNotification));
        await refreshUnreadCount();
        return result;
    }

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                loading,
                loadNotifications,
                markNotificationAsRead,
                deleteSingleNotification,
                clearAllNotifications
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationContext);
}